import React, { FC } from "react";

import { faBars } from "@fortawesome/pro-solid-svg-icons/faBars";
import { faChartSimple } from "@fortawesome/pro-solid-svg-icons/faChartSimple";
import { faEquals } from "@fortawesome/pro-solid-svg-icons/faEquals";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import Info from "./Info";
import OptionInfo from "./OptionInfo";

const messages = defineMessages({
  equalIntervalDescription: {
    id: "map.colorization.createEditSidebar.styleEditor.steps.classificationMethod.tooltip.equalInterval.description",
    defaultMessage:
      "Breakpoints are set equal distance apart for clearer visualization and comprehension. Ideal for looking at how big things are compared to each other. Less effective if the data is clustered on one end or if you're trying to spot outliers.",
  },
  equalIntervalTitle: {
    id: "map.colorization.createEditSidebar.styleEditor.steps.classificationMethod.tooltip.equalInterval.title",
    defaultMessage: "Equal Interval",
  },
  manualDescription: {
    id: "map.colorization.createEditSidebar.styleEditor.steps.classificationMethod.tooltip.manual.description",
    defaultMessage:
      "Break points are manually adjusted and do not dynamically change based on the data values.",
  },
  manualTitle: {
    id: "map.colorization.createEditSidebar.styleEditor.steps.classificationMethod.tooltip.manual.title",
    defaultMessage: "Manual",
  },
  naturalBreaksDescription: {
    id: "map.colorization.createEditSidebar.styleEditor.steps.classificationMethod.tooltip.naturalBreaks.description",
    defaultMessage:
      "Identifies natural data clusters to group similar items together. Most effective with unevenly distributed data. Less effective for comparisons among datasets because each legend’s buckets are uniquely divided.",
  },
  naturalBreaksTitle: {
    id: "map.colorization.createEditSidebar.styleEditor.steps.classificationMethod.tooltip.naturalBreaks.title",
    defaultMessage: "Natural Breaks",
  },
  quantileDescription: {
    id: "map.colorization.createEditSidebar.styleEditor.steps.classificationMethod.tooltip.quantile.description",
    defaultMessage:
      "Divides data into equal-sized buckets. Optimal for evenly distributed data. Less effective for comparing buckets, due to uneven distribution.",
  },
  quantileTitle: {
    id: "map.colorization.createEditSidebar.styleEditor.steps.classificationMethod.tooltip.quantile.title",
    defaultMessage: "Quantile",
  },
  title: {
    id: "map.colorization.createEditSidebar.styleEditor.steps.classificationMethod.tooltip",
    defaultMessage: "About classification methods",
  },
});

export interface ClassificationMethodInfoProps {}

const ClassificationMethodInfo: FC<ClassificationMethodInfoProps> = () => {
  const intl = useIntl();

  return (
    <Info title={intl.formatMessage(messages.title)}>
      <OptionInfo
        description={intl.formatMessage(messages.manualDescription)}
        icon={<FontAwesomeIcon icon={faPen} />}
        title={intl.formatMessage(messages.manualTitle)}
      />{" "}
      <OptionInfo
        description={intl.formatMessage(messages.equalIntervalDescription)}
        icon={<FontAwesomeIcon icon={faEquals} />}
        title={intl.formatMessage(messages.equalIntervalTitle)}
      />{" "}
      <OptionInfo
        description={intl.formatMessage(messages.quantileDescription)}
        icon={<FontAwesomeIcon icon={faBars} rotation={90} />}
        title={intl.formatMessage(messages.quantileTitle)}
      />{" "}
      <OptionInfo
        description={intl.formatMessage(messages.naturalBreaksDescription)}
        icon={<FontAwesomeIcon icon={faChartSimple} />}
        title={intl.formatMessage(messages.naturalBreaksTitle)}
      />
    </Info>
  );
};

export default ClassificationMethodInfo;
